import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type {
  ButtonHTMLAttributes,
  ComponentProps,
  InputHTMLAttributes,
  MouseEvent as ReactMouseEvent,
  ReactNode,
} from 'react';
import { Check, X } from 'lucide-react';

/**
 * Primitivos da interface.
 *
 * Poucos e pequenos de propósito (decisão 10): uma biblioteca de componentes
 * traria dezenas de arquivos e um visual que não é o nosso. O que passar daqui
 * é utilitário Tailwind escrito na própria tela.
 *
 * Decisão 43: o visual é o da marca de hoje — escuro, com o azul da Pure
 * Layout como luz. A hierarquia continua vindo de peso e espaço; o painel é
 * um traço de 1px sobre a superfície, com um realce de 1px no topo, e o
 * brilho azul fica para o que se clica.
 */

export function cx(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ');
}

// --- Botão -------------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'quiet-danger';
  size?: 'sm' | 'md';
  loading?: boolean;
};

const BUTTON_BASE =
  'inline-flex shrink-0 items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap ' +
  'transition-[background,box-shadow,border-color,color,transform] duration-150 ' +
  'disabled:cursor-not-allowed disabled:opacity-45 disabled:shadow-none';

const BUTTON_SIZES = { sm: 'h-8 px-3 text-xs [&_svg]:size-3.5', md: 'h-9 px-4 text-sm [&_svg]:size-4' };

const BUTTON_VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
  // O gradiente da marca, só no botão que avança o trabalho.
  primary:
    'bg-[image:var(--grad-button)] text-white shadow-[var(--shadow-glow)] enabled:hover:shadow-[var(--shadow-glow-hover)] ' +
    'motion-safe:enabled:hover:-translate-y-px',
  secondary:
    'border border-[var(--color-line-strong)] bg-[rgb(255_255_255/0.03)] text-[var(--color-ink)] ' +
    'enabled:hover:border-[rgb(255_255_255/0.28)] enabled:hover:bg-[rgb(255_255_255/0.06)]',
  ghost: 'text-[var(--color-ink-soft)] enabled:hover:bg-[var(--color-hover)] enabled:hover:text-[var(--color-ink)]',
  danger: 'bg-[var(--color-danger)] text-[#1a0308] enabled:hover:brightness-110',
  'quiet-danger': 'text-[var(--color-danger)] enabled:hover:bg-[var(--color-danger-soft)]',
};

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(BUTTON_BASE, BUTTON_SIZES[size], BUTTON_VARIANTS[variant], className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span
      role={label ? 'status' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      className="inline-block size-3.5 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

// --- Campos --------------------------------------------------------------------

/** A classe de todo campo de texto: a mesma no input, no select e no textarea. */
export const controlClass =
  'w-full rounded-[var(--radius-sm)] border bg-[rgb(255_255_255/0.03)] px-3 text-sm text-[var(--color-ink)] ' +
  'placeholder:text-[var(--color-ink-faint)] transition-[border-color,box-shadow] ' +
  'hover:border-[rgb(255_255_255/0.22)] focus:outline-none focus-visible:border-[var(--color-brand)] ' +
  'focus-visible:shadow-[0_0_0_3px_rgb(10_92_255/0.28)] disabled:opacity-50';

type FieldProps = InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  error?: string;
};

function fieldId(label: string, id?: string): string {
  // O id é obrigatório para o <label> apontar para o campo; sem ele, clicar no
  // rótulo não foca o campo e leitores de tela não associam os dois.
  return id ?? `field-${label.toLowerCase().replace(/\W+/g, '-')}`;
}

function FieldFrame({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs font-medium text-[var(--color-ink-soft)]">
        {label}
      </label>
      {children}
      {error ? (
        <span id={`${id}-error`} className="text-xs text-[var(--color-danger)]">
          {error}
        </span>
      ) : hint ? (
        <span id={`${id}-hint`} className="text-xs text-[var(--color-ink-faint)]">
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export function Field({ label, hint, error, id, className, ...rest }: FieldProps) {
  const inputId = fieldId(label, id);
  return (
    <FieldFrame id={inputId} label={label} hint={hint} error={error}>
      <input
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
        className={cx(
          controlClass,
          'h-9',
          error ? 'border-[var(--color-danger)]' : 'border-[var(--color-line-strong)]',
          className,
        )}
        {...rest}
      />
    </FieldFrame>
  );
}

/** `ComponentProps` inclui o `ref` (React 19): a fila foca o texto pelo atalho "e". */
type TextareaProps = ComponentProps<'textarea'> & {
  label: string;
  hint?: string;
  error?: string;
  /** Sem o rótulo visível (fica para leitor de tela): quando o contexto já diz o que é. */
  hideLabel?: boolean;
};

/** Texto longo. Era copiado à mão em quatro telas, cada uma com uma borda diferente. */
export function Textarea({ label, hint, error, id, className, hideLabel, rows = 4, ...rest }: TextareaProps) {
  const inputId = fieldId(label, id);
  const control = (
    <textarea
      id={inputId}
      rows={rows}
      aria-invalid={error ? true : undefined}
      aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
      className={cx(
        controlClass,
        'py-2 leading-relaxed',
        error ? 'border-[var(--color-danger)]' : 'border-[var(--color-line-strong)]',
        className,
      )}
      {...rest}
    />
  );
  if (hideLabel) {
    return (
      <>
        <label htmlFor={inputId} className="sr-only">
          {label}
        </label>
        {control}
      </>
    );
  }
  return (
    <FieldFrame id={inputId} label={label} hint={hint} error={error}>
      {control}
    </FieldFrame>
  );
}

export function Select({
  label,
  id,
  value,
  onChange,
  options,
  hint,
}: {
  label: string;
  id: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  hint?: string;
}) {
  return (
    <FieldFrame id={id} label={label} hint={hint}>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className={cx(controlClass, 'h-9 border-[var(--color-line-strong)] pr-8')}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value} className="bg-[var(--color-raised)]">
            {option.label}
          </option>
        ))}
      </select>
    </FieldFrame>
  );
}

/** Caixa de marcar com o rótulo clicável e a explicação embaixo. */
export function Checkbox({
  label,
  checked,
  onChange,
  hint,
  disabled,
  id,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: ReactNode;
  disabled?: boolean;
  id?: string;
}) {
  return (
    <label className={cx('flex items-start gap-2.5 text-sm', disabled ? 'opacity-50' : 'cursor-pointer')}>
      <span className="relative mt-0.5 inline-flex">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className="peer size-4 shrink-0 appearance-none rounded-[5px] border border-[var(--color-line-strong)] bg-[rgb(255_255_255/0.03)] transition-colors checked:border-[var(--color-brand)] checked:bg-[var(--color-brand)]"
        />
        <Check
          aria-hidden="true"
          strokeWidth={3}
          className="pointer-events-none absolute inset-0.5 size-3 text-white opacity-0 peer-checked:opacity-100"
        />
      </span>
      <span className="flex flex-col gap-0.5">
        <span className="text-[var(--color-ink)]">{label}</span>
        {hint && <span className="text-xs text-[var(--color-ink-faint)]">{hint}</span>}
      </span>
    </label>
  );
}

/**
 * Escolha de uma entre poucas opções vizinhas (período, visão). Substitui o
 * `PeriodPicker` que existia duas vezes e o par de botões da listagem.
 */
export function Segmented<V extends string | number>({
  value,
  onChange,
  options,
  label,
  size = 'md',
}: {
  value: V;
  onChange: (value: V) => void;
  options: readonly { value: V; label: ReactNode }[];
  label: string;
  size?: 'sm' | 'md';
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="inline-flex shrink-0 rounded-full border border-[var(--color-line)] bg-[rgb(255_255_255/0.02)] p-0.5"
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(option.value)}
            className={cx(
              'rounded-full font-medium whitespace-nowrap transition-colors',
              size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-xs',
              active
                ? 'bg-[var(--color-brand-soft)] text-[var(--color-ink)] shadow-[inset_0_0_0_1px_rgb(10_92_255/0.5)]'
                : 'text-[var(--color-ink-soft)] hover:text-[var(--color-ink)]',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

// --- Estrutura da página ------------------------------------------------------------

/**
 * Cabeçalho de página: título em Sora, uma linha do que a página serve, as
 * ações à direita. A página diz onde você está uma vez, e os painéis abaixo
 * não repetem. `emphasis` é a frase em itálico serifado da marca — use em
 * uma tela, não em todas.
 */
export function PageHeader({
  title,
  emphasis,
  description,
  actions,
  children,
}: {
  title: ReactNode;
  emphasis?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="mb-7 flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <h1 className="text-[28px] leading-[1.15] font-semibold tracking-[-0.02em] text-[var(--color-ink)]">
            {title}
            {emphasis && (
              <>
                {' '}
                <span className="font-serif font-normal tracking-normal text-[var(--color-serif-blue)] italic">{emphasis}</span>
              </>
            )}
          </h1>
          {description && <p className="mt-1.5 max-w-2xl text-sm text-[var(--color-ink-soft)]">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  );
}

/** Grava a posição do mouse no cartão: é o que o `.spot` do CSS usa para o brilho. */
export function spotlight(event: ReactMouseEvent<HTMLElement>): void {
  const target = event.currentTarget;
  const rect = target.getBoundingClientRect();
  target.style.setProperty('--mx', `${event.clientX - rect.left}px`);
  target.style.setProperty('--my', `${event.clientY - rect.top}px`);
}

export function Panel({
  title,
  description,
  actions,
  children,
  className,
  flush = false,
  id,
}: {
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Sem recuo interno: para tabela que encosta nas bordas. */
  flush?: boolean;
  id?: string;
}) {
  return (
    <section
      id={id}
      className={cx(
        'min-w-0 rounded-[var(--radius-lg)] border border-[var(--color-line)] bg-[var(--color-surface)] shadow-[var(--shadow-panel)]',
        className,
      )}
    >
      {(title || actions) && (
        <header className="flex flex-wrap items-start justify-between gap-3 px-5 pt-4 pb-1">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-[var(--color-ink)]">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-[var(--color-ink-soft)]">{description}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className={flush ? 'pt-2' : 'px-5 pt-3 pb-5'}>{children}</div>
    </section>
  );
}

export function Alert({ tone = 'danger', children }: { tone?: 'danger' | 'warn' | 'ok'; children: ReactNode }) {
  const tones: Record<string, string> = {
    danger: 'border-[var(--color-danger)] bg-[var(--color-danger-soft)] text-[#ffc2cd]',
    warn: 'border-[var(--color-warn)] bg-[var(--color-warn-soft)] text-[#fbe0a8]',
    ok: 'border-[var(--color-ok)] bg-[var(--color-ok-soft)] text-[#b6f0d2]',
  };
  return (
    <p role="alert" className={cx('rounded-[var(--radius-sm)] border-l-2 px-3 py-2 text-sm', tones[tone])}>
      {children}
    </p>
  );
}

/** Vazio explicado, nunca uma tela em branco: diz o que fazer para preenchê-la. */
export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-start gap-2 px-1 py-8">
      {icon && (
        <span className="mb-1 inline-flex size-10 items-center justify-center rounded-[var(--radius-md)] border border-[rgb(10_92_255/0.3)] bg-[var(--color-brand-soft)] text-[var(--color-brand-text)] [&_svg]:size-5">
          {icon}
        </span>
      )}
      <p className="font-display text-[15px] font-semibold text-[var(--color-ink)]">{title}</p>
      {description && <p className="max-w-md text-sm text-[var(--color-ink-soft)]">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/** Rótulo curto de estado. O tom diz o significado; o texto diz o quê. */
export function Badge({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'brand' | 'ok' | 'warn' | 'danger' | 'night';
  children: ReactNode;
}) {
  const tones: Record<string, string> = {
    neutral: 'bg-[rgb(255_255_255/0.06)] text-[var(--color-ink-soft)]',
    brand: 'bg-[var(--color-brand-soft)] text-[var(--color-brand-text)]',
    ok: 'bg-[var(--color-ok-soft)] text-[var(--color-ok)]',
    warn: 'bg-[var(--color-warn-soft)] text-[var(--color-warn)]',
    danger: 'bg-[var(--color-danger-soft)] text-[var(--color-danger)]',
    night: 'bg-[var(--color-ink)] text-[var(--color-canvas)]',
  };
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        tones[tone],
      )}
    >
      {children}
    </span>
  );
}

/** Uma tecla de atalho, como aparece no teclado. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-[5px] border border-[var(--color-line-strong)] bg-[rgb(255_255_255/0.04)] px-1 font-sans text-[11px] font-medium text-[var(--color-ink-soft)]">
      {children}
    </kbd>
  );
}

/** Espaço reservado enquanto carrega — mostra a forma do que vem, em vez de um giro no vazio. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cx('skeleton block rounded-[var(--radius-sm)]', className)} />;
}

/** Um número com o rótulo do que ele conta, e a explicação embaixo. */
export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'warn' | 'danger' | 'ok';
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs text-[var(--color-ink-soft)]">{label}</span>
      <span
        className={cx(
          'font-display tabular text-[26px] leading-none font-semibold',
          tone === 'warn'
            ? 'text-[var(--color-warn)]'
            : tone === 'danger'
              ? 'text-[var(--color-danger)]'
              : tone === 'ok'
                ? 'text-[var(--color-ok)]'
                : 'text-[var(--color-ink)]',
        )}
      >
        {value}
      </span>
      {hint && <span className="text-xs text-[var(--color-ink-faint)]">{hint}</span>}
    </div>
  );
}

/** Classes das tabelas: cabeçalho discreto, linha com hover. Uma só para as seis telas que têm tabela. */
export const table = {
  wrap: 'overflow-x-auto',
  table: 'w-full min-w-max border-collapse text-sm',
  th: 'px-4 py-2.5 text-left text-xs font-medium whitespace-nowrap text-[var(--color-ink-faint)]',
  row: 'border-t border-[var(--color-line)] transition-colors hover:bg-[var(--color-hover)]',
  td: 'px-4 py-2.5 align-middle',
};

// --- Diálogo -------------------------------------------------------------------------

/**
 * Diálogo modal sobre o `<dialog>` nativo: foco preso, Esc fecha e o leitor de
 * tela anuncia — de graça, sem biblioteca. Nunca `window.confirm`: ele trava a
 * aba e não diz o que vai acontecer em números.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      className="enter-up m-auto w-[min(520px,calc(100vw-32px))] rounded-[var(--radius-lg)] border-0 bg-[var(--color-raised)] p-0 text-[var(--color-ink)] shadow-[var(--shadow-pop)]"
    >
      {open && (
        <div className="flex flex-col">
          <div className="flex items-start justify-between gap-4 px-6 pt-5">
            <div className="min-w-0">
              <h2 className="text-lg font-semibold">{title}</h2>
              {description && <p className="mt-1 text-sm text-[var(--color-ink-soft)]">{description}</p>}
            </div>
            <button
              type="button"
              aria-label="Fechar"
              onClick={onClose}
              className="-mr-2 inline-flex size-8 shrink-0 items-center justify-center rounded-full text-[var(--color-ink-faint)] hover:bg-[var(--color-hover)] hover:text-[var(--color-ink)]"
            >
              <X className="size-4" aria-hidden="true" />
            </button>
          </div>
          {children && <div className="px-6 pt-4">{children}</div>}
          <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-[var(--color-line)] px-6 py-4">
            {footer ?? <Button onClick={onClose}>Fechar</Button>}
          </div>
        </div>
      )}
    </dialog>
  );
}

// --- Avisos (toast) ------------------------------------------------------------------

interface ToastInput {
  message: string;
  tone?: 'ok' | 'danger';
  /** Ação do aviso — o "Desfazer" da exclusão em lote. */
  action?: { label: string; onClick: () => void | Promise<void> };
  /** Quanto tempo fica na tela. Com ação, mais: desfazer precisa de tempo para ler. */
  durationMs?: number;
}

interface ToastItem extends ToastInput {
  id: number;
}

const ToastContext = createContext<(toast: ToastInput) => void>(() => {});

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(0);

  const dismiss = useCallback((id: number) => setItems((current) => current.filter((item) => item.id !== id)), []);

  const push = useCallback(
    (toast: ToastInput) => {
      const id = ++next.current;
      setItems((current) => [...current.slice(-2), { ...toast, id }]);
      window.setTimeout(() => dismiss(id), toast.durationMs ?? (toast.action ? 12_000 : 5_000));
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col items-center gap-2 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:items-end"
      >
        {items.map((item) => (
          <div
            key={item.id}
            role="status"
            className="enter-up pointer-events-auto flex max-w-md items-center gap-3 rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-raised)] py-2.5 pr-2 pl-4 text-sm text-[var(--color-ink)] shadow-[var(--shadow-pop)]"
          >
            <span
              aria-hidden="true"
              className={cx(
                'size-2 shrink-0 rounded-full',
                item.tone === 'danger'
                  ? 'bg-[var(--color-danger)] shadow-[0_0_10px_var(--color-danger)]'
                  : 'bg-[var(--color-cyan)] shadow-[0_0_10px_var(--color-cyan)]',
              )}
            />
            <span className="min-w-0 flex-1">{item.message}</span>
            {item.action && (
              <button
                type="button"
                className="rounded-full px-2.5 py-1 font-medium text-[var(--color-cyan)] hover:bg-[var(--color-hover)]"
                onClick={() => {
                  dismiss(item.id);
                  void item.action!.onClick();
                }}
              >
                {item.action.label}
              </button>
            )}
            <button
              type="button"
              aria-label="Fechar aviso"
              className="inline-flex size-7 items-center justify-center rounded-full text-[var(--color-ink-faint)] hover:bg-[var(--color-hover)] hover:text-[var(--color-ink)]"
              onClick={() => dismiss(item.id)}
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// --- Trilho de etapas -----------------------------------------------------------------

/**
 * O trilho da venda: etapas em sequência, cada uma com o seu número.
 *
 * Só cabe aqui porque o conteúdo É uma sequência: quem está em "a contatar"
 * passa para "aguardando", e depois para "responderam". A etapa em foco ganha
 * a luz azul da marca; as outras ficam na superfície.
 */
export function StageRail<K extends string>({
  stages,
  active,
  onSelect,
  label,
}: {
  stages: { key: K; label: string; count: number | undefined }[];
  active?: K;
  onSelect?: (key: K) => void;
  label: string;
}) {
  return (
    <nav aria-label={label} className="-mx-1 overflow-x-auto px-1 pb-1">
      <ol className="flex min-w-max gap-2 sm:min-w-0">
        {stages.map((stage, index) => {
          const isActive = stage.key === active;
          const content = (
            <>
              <span className="flex w-full items-center justify-between gap-2">
                <span
                  className={cx(
                    'text-xs font-medium whitespace-nowrap',
                    isActive ? 'text-[var(--color-brand-text)]' : 'text-[var(--color-ink-soft)]',
                  )}
                >
                  {stage.label}
                </span>
                <span aria-hidden="true" className="font-display tabular text-[11px] text-[var(--color-ink-faint)]">
                  {String(index + 1).padStart(2, '0')}
                </span>
              </span>
              <span className="font-display tabular text-[28px] leading-none font-semibold text-[var(--color-ink)]">
                {stage.count ?? '–'}
              </span>
            </>
          );
          const shape = cx(
            'spot relative flex w-full min-w-[132px] flex-col items-start gap-2.5 overflow-hidden rounded-[var(--radius-md)] border px-4 py-3 text-left transition-[border-color,background]',
            isActive
              ? 'border-[rgb(10_92_255/0.55)] bg-[linear-gradient(180deg,rgb(10_92_255/0.16),rgb(10_92_255/0.04))] shadow-[inset_0_1px_0_rgb(0_180_255/0.35)]'
              : 'border-[var(--color-line)] bg-[var(--color-surface)] hover:border-[var(--color-line-strong)]',
          );
          return (
            <li key={stage.key} className="flex flex-1">
              {onSelect ? (
                <button
                  type="button"
                  className={shape}
                  aria-current={isActive ? 'step' : undefined}
                  onMouseMove={spotlight}
                  onClick={() => onSelect(stage.key)}
                >
                  {content}
                </button>
              ) : (
                <div className={shape}>{content}</div>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

// --- Nota ----------------------------------------------------------------------------

function scoreTone(value: number): string {
  // 70 é o corte que a listagem sempre destacou; 50 é a nota mínima para um
  // produto virar oportunidade (Opportunity Engine). Abaixo disso, apagado.
  return value >= 70
    ? 'text-[var(--color-score-good)]'
    : value >= 50
      ? 'text-[var(--color-ink)]'
      : 'text-[var(--color-score-low)]';
}

/** Nota de 0 a 100 com a faixa em cor — e o número sempre visível. */
export function ScoreValue({ value, size = 'md' }: { value: number | null | undefined; size?: 'md' | 'lg' }) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-[var(--color-ink-faint)]">sem nota</span>;
  }
  return (
    <span className={cx('font-display tabular font-semibold', size === 'lg' ? 'text-3xl' : 'text-sm', scoreTone(value))}>
      {value}
    </span>
  );
}

/**
 * A nota num anel: o número no meio, a volta preenchida até ele. Para onde a
 * nota é o dado principal da linha (listagem, fila). A cor segue as faixas de
 * `ScoreValue`; o número continua lá para quem não distingue cor.
 */
export function ScoreRing({ value, size = 36 }: { value: number | null | undefined; size?: number }) {
  if (value === null || value === undefined) {
    return <span className="text-xs text-[var(--color-ink-faint)]">sem nota</span>;
  }
  const stroke = 3;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const color = value >= 70 ? 'var(--color-score-good)' : value >= 50 ? 'var(--color-brand-text)' : 'var(--color-score-low)';
  return (
    <span className="relative inline-flex shrink-0 items-center justify-center" style={{ width: size, height: size }} title={`Nota ${value} de 100`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="rgb(255 255 255 / 0.08)" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(Math.max(0, Math.min(100, value)) / 100) * circumference} ${circumference}`}
        />
      </svg>
      <span className={cx('font-display tabular absolute text-[11px] font-semibold', scoreTone(value))}>{value}</span>
    </span>
  );
}
